import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, View, ScrollView, Text, Platform, TouchableOpacity } from 'react-native';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import TextField from '../controls/TextBox';
import NumberField from '../controls/NumberField';
import PaperDates from '../controls/datePicker-paperDates';
import ButtonControl from '../controls/Button';
import { createItem, getPackageStatus, proceedToCreate, updateItem, getInputCheckConfig } from '../service/dataOperations';
import { useAuth } from '../../../../providers/AuthProvider';
import { useClient } from '../../../../providers/ClientProvider';
import NotificationBanner, { NotificationType } from '../controls/NotificationBanner';
import formCustomStyles from './formStyles';
import { getEnergyLiterToM3, getQuantityCalculation, getQuantityGallonToM3, getSoilTonneToM3 } from '../controls/BusinessLogic';
import * as FileSystem from 'expo-file-system';
import * as MediaLibrary from 'expo-media-library';
import DocPicker from '../controls/DocPicker';
import { IconButton, MD3Colors, Portal, Tooltip } from 'react-native-paper';
import { ConfirmTop } from '../../../Common/ConfirmBoxTop';
import ReactDropdownSingleSelect from '../controls/Dropdown-Single';
import ComplianceTable from '../service/monitoringCompliance';
import AttachmentViewerModal from '../../LogControllers/AttachmentViewerModal';

interface PaperFormProps {
  fields: Array<any>;
  formMode: string;
  showNotification?: (message: string, type: NotificationType) => void;
  selectedRow: Array<any>;
  closeForm: () => void;
  siteId: string;
  logTitle: string;
  updateFormState: () => void;
  ItemCreated: (Item: Array<any>) => void;
  Permissions?: any;
  chartVisibility?: boolean
  packageId?: string;
  packageStatusItems?: any;
}

const PaperForm: React.FC<PaperFormProps> = React.memo((props) => {

  const [formStyle, setFormStyle] = useState(styles.formContainerMobile);
  const [formMode, setFormMode] = useState(props.formMode);
  const [Quantity, setQuantity] = useState(null);
  const [QuantityTonne, setQuantityTonne] = useState(null);
  const [fields, setFields] = useState(props.fields ?? []);
  const [quantityWarningAck, setQuantityWarningAck] = useState<boolean>(false);
  const [logVerificationConfig, setLogVerificationConfig] = useState<any>(undefined);

  const logLimit = logVerificationConfig?.[props.logTitle] || logVerificationConfig?.Default;
  const currentThreshold = logLimit?.Threshold || 1000;
  const currentUnitField = logLimit?.Unit || "Quantitym3"; // Use the dynamic field name from API
  const warningMessage = logLimit?.WarningMessage || logVerificationConfig?.WarningMessage || `This value seems unusually high for ${props.logTitle}. Please verify.`;

  const [loadForm, setLoadForm] = useState<boolean>(false);
  const [itemId, setItemId] = useState(null);
  const [d, setD] = useState<any>('');

  const [docWarning, setDocWarning] = useState<boolean>(null);
  const [warningText, setWarningText] = useState<string>(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileLoad, setFileLoad] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>();
  const [visible, setVisible] = React.useState(false);
  const [fileApi, setFileApi] = useState(null);
  const [downloadedFileUri, setDownloadedFileUri] = useState(null);
  const [docType, setDocType] = useState(null);
  const [isViewerVisible, setIsViewerVisible] = useState<boolean>(false);
  const [viewerBlob, setViewerBlob] = useState<Blob | null>(null);
  const [viewerFileName, setViewerFileName] = useState<string>("");
  const [viewerLoading, setViewerLoading] = useState<boolean>(false);
  //const [packageStatusItems, setPackageStatusItems] = useState(undefined);
  let item: any;
  //let fields = props.fields;
  useEffect(() => {
    const fetchData = async () => {
      const accessToken = await getAccessToken();
      //const _packageStatusItems = props.packageId && await getPackageStatus(accessToken, clientAPIURL, props.packageId);
      //setPackageStatusItems(_packageStatusItems);
      let formFields = props.fields;
      if (formMode === 'Edit' || formMode === 'View') {
        item = props.selectedRow[0] && props.selectedRow[0];
        formFields = item && props.fields.map((field) => {
          const defaultValue = item[field.key] && item[field.key];
          return { ...field, defaultValue };
        });
        const AttachmentColumn = formFields && formFields.find((c) => c.subType === "attachment");
        if (AttachmentColumn && AttachmentColumn.defaultValue) {
          setUploadedFiles(JSON.parse(AttachmentColumn.defaultValue));
        }
        item?.ID && setFileApi(encodeURI(clientAPIURL + "api/sites/" + props.siteId + "/lists/" + props.logTitle + "/items/" + item.ID + "/attachments/"));
        formFields && setFields(formFields);
        const inputChecks = await getInputCheckConfig(accessToken, clientAPIURL, props.packageId);
        console.log("inputChecks", inputChecks);
        setLogVerificationConfig(inputChecks);
        setLoadForm(true);
        item?.ID && setItemId(item.ID);
      }
      else if (formMode === 'New') {
        const accessToken = await getAccessToken();
        const inputChecks = await getInputCheckConfig(accessToken, clientAPIURL, props.packageId);
        setLogVerificationConfig(inputChecks);
        setLoadForm(true)
      }
    }
    fetchData();
  }, [formMode, props.fields]);
  const { register, setValue, handleSubmit, control, reset, watch, getValues, setError, clearErrors, formState: { errors } } = useForm();

  const logTitle = props.logTitle;

  const isWaterLog = logTitle === "Water Log" || logTitle === "Wastewater Tracking Log";
  const isSoilLog = logTitle === "Imported Soil Log" || logTitle === "Spoil Tracking Log";
  const isEnergyLog = logTitle === "Energy Log";

  const quantityM3 = (isWaterLog || isSoilLog || isEnergyLog) ? useWatch({ control, name: "Quantitym3" }) : undefined;
  const quantityGallon = isWaterLog ? useWatch({ control, name: "QuantityGallon" }) : undefined;
  const quantityTonne = isSoilLog ? useWatch({ control, name: "QuantityTonne" }) : undefined;
  const quantityLiter = isEnergyLog ? useWatch({ control, name: "QuantityLiters" }) : undefined;

  const isProgrammaticUpdate = useRef(false);

  const selectedDate = props.logTitle === 'Environmental Issues Observations'
    || props.logTitle === 'Package Status'
    || props.logTitle === 'Permit And Licenses'
    || props.logTitle === 'Contracts'
    || props.logTitle === 'Recycled Materials Log'
    || props.logTitle === 'Regional Materials Log'
    ? undefined : watch("Date");

  useEffect(() => {
    // Reset acknowledgment if the dynamic quantity value changes
    setQuantityWarningAck(false);
  }, [watch(currentUnitField)]);

  useEffect(() => {
    if (!isProgrammaticUpdate.current) {
      if (selectedDate) {
        const allow = proceedToCreate(selectedDate, props.packageStatusItems);
        if (!allow) {
          setValue("Date", null);
          setError("Date", { type: "manual", message: "Adding / Editing for this period is locked." });
          isProgrammaticUpdate.current = true;
          //setToastText("Invalid Date: The selected date is outside the package active period.");
          //setToastVisibility(true);
        }
        else {
          //how to clear error that is set above?
          clearErrors("Date");
        }
      }
    }
    else {
      isProgrammaticUpdate.current = false;
    }
  }, [selectedDate]);

  React.useEffect(() => {
    if (formMode !== "New") return; // only for new form

    fields.forEach((f) => {
      if (f.subType === "choice" && f.options) {
        const optionsArray = f.options.split("#;#").map((o: string) => o.trim());

        if (optionsArray.length === 1) {
          // only one choice, set it as default
          setValue(f.key, optionsArray[0]);
        }
      }
    });
  }, [fields, formMode, setValue]);

  // Water/Wastewater: m3 → Gallon
  useEffect(() => {

    if (!isWaterLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityM3 !== undefined && quantityM3 !== null && quantityM3 !== "") {
        const m3 = parseFloat(quantityM3);
        if (!isNaN(m3)) {
          isProgrammaticUpdate.current = true;
          setValue("QuantityGallon", getQuantityCalculation(logTitle, m3));
        }
      } else {
        //setValue("QuantityGallon", null);
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityM3, isWaterLog]);

  // Water/Wastewater: Gallon → m3
  useEffect(() => {

    if (!isWaterLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityGallon !== undefined && quantityGallon !== null && quantityGallon !== "") {
        const gallon = parseFloat(quantityGallon);
        if (!isNaN(gallon)) {
          isProgrammaticUpdate.current = true;
          setValue("Quantitym3", getQuantityGallonToM3(logTitle, gallon));
        }
      } else {
        //setValue("Quantitym3", null);
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityGallon, isWaterLog]);


  // Soil/Spoil: m3 → Tonne
  useEffect(() => {

    if (!isSoilLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityM3 !== undefined && quantityM3 !== null && quantityM3 !== "") {
        const m3 = parseFloat(quantityM3);
        if (!isNaN(m3)) {
          isProgrammaticUpdate.current = true;
          setValue("QuantityTonne", getQuantityCalculation(logTitle, m3));
        }
      } else {
        //setValue("QuantityTonne", '');
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityM3, isSoilLog]);

  // Soil/Spoil: Tonne → m3
  useEffect(() => {

    if (!isSoilLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityTonne !== undefined && quantityTonne !== null && quantityTonne !== "") {
        const tonne = parseFloat(quantityTonne);
        if (!isNaN(tonne)) {
          isProgrammaticUpdate.current = true;
          setValue("Quantitym3", getSoilTonneToM3(logTitle, tonne)); // or create getTonneToM3()
        }
      } else {
        //setValue("Quantitym3", '');
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityTonne, isSoilLog]);

  // Energy: m3 → Liter
  useEffect(() => {
    if (!isEnergyLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityM3 !== undefined && quantityM3 !== null && quantityM3 !== "") {
        const m3 = parseFloat(quantityM3);
        if (!isNaN(m3)) {
          isProgrammaticUpdate.current = true;
          setValue("QuantityLiters", getQuantityCalculation(logTitle, m3));
        }
      } else {
        //setValue("QuantityLiters", null);
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityM3, isEnergyLog]);

  // Energy: Liter → m3
  useEffect(() => {
    if (!isEnergyLog) return;

    if (!isProgrammaticUpdate.current) {
      if (quantityLiter !== undefined && quantityLiter !== null && quantityLiter !== "") {
        const liter = parseFloat(quantityLiter);
        if (!isNaN(liter)) {
          isProgrammaticUpdate.current = true;
          setValue("Quantitym3", getEnergyLiterToM3(logTitle, liter));
        }
      } else {
        //setValue("Quantitym3", null);
      }
    } else {
      isProgrammaticUpdate.current = false;
    }
  }, [quantityLiter, isEnergyLog]);


  const [toastVisibility, setToastVisibility] = useState<boolean>(false)
  const [toastText, setToastText] = useState<string>("");
  const [toastType, setToastType] = useState<NotificationType>('info');
  const { getAccessToken } = useAuth();
  const { clientName, clientAPIURL } = useClient();
  function MonthYear(value: Date) {
    if (value) {
      const date = new Date(value);
      const timezoneOffset = 4 * 60; // UTC +4 timezone offset in minutes
      const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
      const month = adjustedDate.toLocaleString('en-US', { month: 'short' });
      const year = adjustedDate.getFullYear();
      return month + "-" + year;
    } else {
      return null;
    }
  }
  const onSubmit = async (d: any) => {
    setToastText("Submitting...");
    setToastType('submitting');
    setToastVisibility(true);
    const MonthANDYear = d.Date && d.Date != null ? MonthYear(d.Date) : null;
    const Month = MonthANDYear === null ? null : MonthANDYear.split('-')[0];
    const Year = MonthANDYear === null ? null : MonthANDYear.split('-')[1];
    const uploadedFilesJson = JSON.stringify(uploadedFiles);
    const data = MonthANDYear ? { ...d, 'Attachments': uploadedFilesJson, Month, Year } : { ...d, 'Attachments': uploadedFilesJson };
    //setD(JSON.stringify(data));
    let oItem = data;
    const result = Object.keys(data).map((key) => {
      const column = fields.find((c) => c.key === key);
      if (!column) {
        return null;
      }
      const value = data[key];
      const dataType = column.datatype.toLowerCase();
      if (dataType === 'datetimes') {
        delete oItem.key;
        oItem = { ...oItem, key: value };
      }

      //const sanitize = (val: any) =>
      //typeof val === 'string' ? val.replace(/<[^>]*>?/gm, '') : val;

      const formattedValue =
        dataType === 'strings'
          ? value
          : dataType === 'numbers'
            ? Number(value)
            : dataType === 'datetimes'
              ? value
              : null;

      return {
        column_Name: column.key,
        [`${dataType}_Value`]: formattedValue,
        dataype: dataType,
      };
    }).filter((x) => x !== null);

    let response: any;

    if (formMode === 'Edit') {
      const accessToken: any = await getAccessToken();
      try {
        response = await updateItem(props.logTitle, accessToken, clientAPIURL, props.siteId, itemId, result);
      }
      catch (err: any) {
        if (err.response?.status === 400) {
          console.error("Error updating item:", err);
          setToastText("Operation Failed: " + err.message);
          setToastType('error');
          setToastVisibility(true);
          return;
        } else {
          setToastText("Something went wrong. Please try again.");
          setToastType('error');
          setToastVisibility(true);
        }
      }
    } else {
      const accessToken: any = await getAccessToken();
      try {
        response = await createItem(props.logTitle, accessToken, clientAPIURL, props.siteId, result);
      }
      catch (err: any) {
        if (err.response?.status === 400) {
          console.error("Error updating item:", err);
          setToastText("Operation Failed: " + err.message);
          setToastType('error');
          setToastVisibility(true);
          return;
        } else {
          setToastText("Something went wrong. Please try again.");
          setToastType('error');
          setToastVisibility(true);
        }
      }
    }

    //if(response.status === 401 || response.statusText === "Unauthorized" || response.statusText === "Bad Request")
    if (!response) {

      setToastText("Operation Failed: You are not authorized to perform this action.")
      setToastType('error');
      setToastVisibility(true);
    }
    if (response) {
      if (typeof response === 'number' || response.ok === true) {
        {
          setToastText("Your changes are saved.")
          setToastType('success');
          setToastVisibility(true);
          //const dataArray = [data];
          setTimeout(function () {
            if (formMode === 'Edit') {
              //const createdItem = oItem && {...oItem, "ID": itemId, "key": itemId };
              if (oItem) {
                oItem["ID"] = itemId
                oItem["key"] = itemId
              }
              const createdItem = oItem
              props.ItemCreated(createdItem);
            }
            else {
              const createdItem = oItem //&& { ...oItem, "ID": response, "key": response };
              if (oItem) {
                oItem["ID"] = response
                oItem["key"] = response
              }
              props.ItemCreated(createdItem);
            }
          }, 2000)
        }
      }
      else if (response && response.ok === false) {
        setToastText("Operation Failed: One or more inputs contain invalid content.")
        setToastType('error');
        setToastVisibility(true);
        return result
      }
    }
  };

  const onError = (errors: any) => {
    const firstErrorKey = Object.keys(errors)[0];
    const errorField = fields.find(f => f.key === firstErrorKey);
    setToastText(`Please fill in the required field: ${errorField?.title || firstErrorKey}`);
    setToastType('error');
    setToastVisibility(true);
    if (Platform.OS === 'web') {
      const errorElement = document.getElementById(`field-${firstErrorKey}`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const invalidNumber = () => {
    setToastText("Invalid Entry: Please enter valid number to numerical column.")
    setToastType('error');
    setToastVisibility(true);
  }

  const handleClose = () => {
    props.closeForm()
  }

  const handleToastDismiss = () => {
    setToastVisibility(false);
  };

  const onFileUploaded = (fileObj: any) => {
    let parsedFileData = JSON.parse(fileObj);
    setUploadedFiles(prevFiles => [...prevFiles, parsedFileData]);
  }

  const showModal = () => setVisible(true);
  const hideModal = () => setVisible(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [fileId, setFileId] = useState<any>(null);

  function confirmDelete(id: any) {
    setFileId(id)
    showConfirmationBox();
  }
  function showConfirmationBox() {
    setShowConfirmation(!showConfirmation);
  }
  function removeFile() {
    const newFiles = fileId && uploadedFiles.filter(file => file.fileId !== fileId);
    setUploadedFiles(newFiles);
    showConfirmationBox();
  }

  const downloadFile = async (url: string, token: string, fileName: string) => {

    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      const { status } = await MediaLibrary.requestPermissionsAsync();
      if (status !== 'granted') {
        alert('Sorry, we need media library permissions to make this work!');
      }
    }

    const uri = url;
    setFileType(uri.split('.').pop().toLowerCase());
    setDocType(uri.split('.').pop().toLowerCase());
    const fileUri = FileSystem.documentDirectory + fileName;
    let options = {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    };
    console.info('Trying to download ' + uri);
    console.info('Download Loc ' + fileUri);
    FileSystem.makeDirectoryAsync(FileSystem.documentDirectory, { intermediates: true })
      .then(() => {
        FileSystem.downloadAsync(uri, fileUri, options)
          .then(({ uri }) => {
            console.log('Finished downloading to ', uri);
            setDownloadedFileUri(uri);
            showModal();
            setFileLoad(false);
          })
          .catch(error => {
            console.error(error);
          });
      })
      .catch(error => {
        console.error('Error creating directory:', error);
      });
  }
  const openFile = async (fileId: string, fileName: string) => {
    
    const api = fileApi + encodeURIComponent(fileId);
    const accessToken = await getAccessToken();

    if (Platform.OS === "web") {
      setViewerFileName(fileName);
      setIsViewerVisible(true);
      setViewerLoading(true);
      setViewerBlob(null);
      try {
        const response = await fetch(api, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const blob = await response.blob();
        setViewerBlob(blob);
      } catch (err) {
        console.error('Error opening file: ', err);
        setToastText("Failed to load file for preview.");
        setToastType('error');
        setToastVisibility(true);
        setIsViewerVisible(false);
      } finally {
        setViewerLoading(false);
      }
    } else {
      setFileLoad(true);
      downloadFile(api, accessToken, fileName);
    }
  }
  function getMiddleEllipsis(name: string, startChars = 12, endChars = 16) {
    if (!name) return "";

    // If already short enough, return as-is
    if (name.length <= startChars + endChars + 3) return name;

    const start = name.slice(0, startChars);
    const end = name.slice(-endChars);
    return `${start}…${end}`;
  }
  const fieldStyle = formMode === "View" ? formCustomStyles.fieldContianerViewMode : formCustomStyles.fieldContianer
  return (
    loadForm === false ? null :
      <View style={{ flex: 1 }}>
        <Portal>
          <NotificationBanner
            visible={toastVisibility}
            message={toastText}
            type={toastType}
            onDismiss={handleToastDismiss}
            duration={3000}
          />
        </Portal>
        <ScrollView>
            <>
              <View style={[{ flex: 1, margin: 10 }, styles.buttonsContainer]}>
                {
                  formMode === "View"
                    ?
                    // <View style={[{ flex: 1 }, styles.buttonsContainer]}>
                    //   <ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' />
                    // </View>
                    <View>
                      <View style={[{ flex: 1 }, styles.buttonsContainer]}><ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' /></View>
                    </View>
                    :
                    <ScrollView horizontal>
                      <View style={styles.buttonsContainer}>
                        <View style={{}}>
                          <ButtonControl icon="content-save-edit" mode="contained" onPress={handleSubmit(onSubmit, onError)} Title='Submit' />
                        </View>
                        <View style={{}}>
                          <ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' />
                        </View>
                      </View>
                    </ScrollView>
                }
              </View>



              {/* Attachment Section Start */}
              {docWarning === true ?
                <View>
                  <Text style={{ color: 'red' }}>{warningText}</Text>
                </View>
                : null}
              {/* <View style={{ borderWidth: 2, borderColor: '#DDDDDD', paddingLeft: 10 }}> */}
              <View style={{ paddingLeft: 10 }}>
                <View style={{ flexDirection: 'column' }}>
                  {formMode === "View" ? null :
                    <View style={{ padding: 20 }}>
                      <DocPicker formMode={formMode} onFileUploaded={onFileUploaded} clientAPIURL={clientAPIURL} showWarning={setDocWarning} warningText={setWarningText} />
                    </View>
                  }
                  {docWarning === true ?
                    <View>
                      <Text style={{ color: 'red' }}>{warningText}</Text>
                    </View>
                    : null}
                </View>
                <View style={{ paddingLeft: 20 }}>

                  {uploadedFiles.map((file: { fileId: string, fileName: string }, index) => (
                    <View key={index} style={{ flexDirection: "row", alignItems: "center", height: 50, gap: 8 }}>
                      <TouchableOpacity 
                        onPress={() => openFile(file.fileId, file.fileName)} 
                        style={{ flex: 1, minWidth: 0 }}
                        disabled={formMode === "New"}
                      >
                        <Text style={{ marginRight: 10 }}>
                          {index + 1}: <Text style={{ color: formMode === "New" ? '#333' : 'blue', textDecorationLine: formMode === "New" ? 'none' : 'underline' }} numberOfLines={1}>
                            {getMiddleEllipsis(file.fileName, 14, 18)}
                          </Text>
                        </Text>
                      </TouchableOpacity>
                      
                      {formMode !== "View" && (
                        <View style={{ flexShrink: 0 }}>
                          <Tooltip title="Remove attachment">
                            <IconButton
                              icon="close-circle-outline"
                              iconColor={MD3Colors.error50}
                              size={20}
                              onPress={() => confirmDelete(file.fileId)}
                            />
                          </Tooltip>
                        </View>
                      )}
                    </View>
                  ))}
                  {/* {showConfirmation && (
                  <View>

                    <ConfirmBox title={"Confirmation"}
                      message={"Are you sure you want to proceed to delete selected items?"}
                      yesCallback={removeFile}
                      noCallback={showConfirmationBox} />

                  </View>
                )} */}
                  {showConfirmation && (
                    <ConfirmTop
                      visible={showConfirmation}
                      title="Delete Attachment"
                      message="Are you sure you want to permanently delete this attachment? This action cannot be undone."
                      onYes={removeFile}
                      onNo={showConfirmationBox}
                      yesText="Delete"
                      noText="Cancel"
                    />
                  )}
                </View>
                <View style={{ padding: 10 }}></View>
              </View>
              {/* Attachment Section End */}



              {/* <View style={formCustomStyles.allFieldsContainer}> */}
              <View style={{}}>
                {(fields ?? []).map((field, index) => {
                  if (field.hidden === true) {
                    return null
                  }
                  else {
                    switch (field.subType) {
                      case 'text':
                        return (
                          // <Text>{field.key}</Text>
                          <View key={index} nativeID={`field-${field.key}`} style={[fieldStyle]}>
                            <TextField
                              key={field.key.toString()}
                              editable={formMode === 'View' ? false : true}
                              field={field}
                              control={control}
                              styles={styles.input}
                              Ctrl={Controller}
                              errors={errors}
                            //ref={register}
                            />
                          </View>
                        );
                      case 'choice':
                        return (
                          <View key={index} nativeID={`field-${field.key}`} style={[fieldStyle]}>
                            <ReactDropdownSingleSelect
                              field={field}
                              key={field.key.toString()}
                              editable={formMode === 'View' ? false : true}
                              control={control}
                              Ctrl={Controller}
                              errors={errors}
                            />
                            {/* <DropdownControl
                          key={field.key.toString()}
                          editable={formMode === 'View' ? false : true}
                          field={field}
                          control={control}
                          Ctrl={Controller}  
                          errors={errors}                        
                        /> */}
                          </View>
                        );
                      case 'float':
                        return (
                          <View key={index} nativeID={`field-${field.key}`} style={[fieldStyle]}>
                            <NumberField
                              key={field.key.toString()}
                              editable={formMode === 'View' ? false : true}
                              field={field}
                              control={control}
                              styles={styles.input}
                              Ctrl={Controller}
                              errors={errors}
                              logTitle={props.logTitle}
                              fields={fields}
                              setFields={setFields}
                              invalidNumber={invalidNumber}
                            //ref={register}
                            />
                            {Boolean(
                              field.key.toLowerCase().includes("quantity") &&
                              watch(currentUnitField) &&
                              currentThreshold &&
                              parseFloat(watch(currentUnitField)) > currentThreshold &&
                              !quantityWarningAck &&
                              logLimit?.AnomalyDetection !== "No"
                            ) && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff3cd', padding: 8, borderRadius: 4, marginTop: 4 }}>
                                  <MaterialCommunityIcons name="alert-circle-outline" size={16} color="#856404" />
                                  <Text style={{ color: '#856404', fontSize: 12, marginLeft: 6, flex: 1 }}>
                                    {warningMessage}
                                  </Text>
                                  <TouchableOpacity onPress={() => setQuantityWarningAck(true)}>
                                    <Text style={{ color: '#856404', fontSize: 12, fontWeight: 'bold', padding: 4 }}>OK</Text>
                                  </TouchableOpacity>
                                </View>
                              )}
                          </View>
                        );
                      case 'integer':
                        return (
                          <View key={index} nativeID={`field-${field.key}`} style={[fieldStyle]}>
                            <NumberField
                              key={field.key.toString()}
                              editable={formMode === 'View' ? false : true}
                              field={field}
                              control={control}
                              styles={styles.input}
                              Ctrl={Controller}
                              errors={errors}
                              logTitle={props.logTitle}
                              fields={fields}
                              setFields={setFields}
                              invalidNumber={invalidNumber}
                            //ref={register}
                            />
                            {Boolean(
                              field.key.toLowerCase().includes("quantity") &&
                              watch(currentUnitField) &&
                              currentThreshold &&
                              parseFloat(watch(currentUnitField)) > currentThreshold &&
                              !quantityWarningAck &&
                              logLimit?.AnomalyDetection !== "No"
                            ) && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff3cd', padding: 8, borderRadius: 4, marginTop: 4 }}>
                                  <MaterialCommunityIcons name="alert-circle-outline" size={16} color="#856404" />
                                  <Text style={{ color: '#856404', fontSize: 12, marginLeft: 6, flex: 1 }}>
                                    {warningMessage}
                                  </Text>
                                  <TouchableOpacity onPress={() => setQuantityWarningAck(true)}>
                                    <Text style={{ color: '#856404', fontSize: 12, fontWeight: 'bold', padding: 4 }}>OK</Text>
                                  </TouchableOpacity>
                                </View>
                              )}
                          </View>
                        );
                      case 'date':
                        return (
                          <View key={index} nativeID={`field-${field.key}`} style={[fieldStyle, { zIndex: 1 }]}>
                            <PaperDates
                              key={field.key.toString()}
                              field={field}
                              editable={formMode === 'View' ? false : true}
                              control={control}
                              styles={styles.date}
                              Ctrl={Controller}
                              errors={errors}
                            //ref={register}
                            />
                          </View>
                        );
                      default:
                        return null//<View key={index} nativeID={`field-${field.key}`} style={[fieldStyle]}><Text>{field.key}</Text></View>;
                    }
                  }
                }
                )}
              </View>
              <View style={{ margin: 10 }}>
                {
                  formMode === "View"
                    ?
                    // <View style={[{flex:1}, styles.buttonsContainer]}><ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' /></View>
                    <View style={[{ flex: 1 }, styles.buttonsContainer]}><ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' /></View>
                    :
                    <ScrollView horizontal style={{ width: '100%' }}>
                      <View style={styles.buttonsContainer}>
                        <View style={{}}><ButtonControl icon="content-save-edit" mode="contained" onPress={handleSubmit(onSubmit, onError)} Title='Submit' /></View>
                        <View style={{}}><ButtonControl icon="close-circle-outline" mode="contained" onPress={handleClose} Title='Close' /></View>
                      </View>
                    </ScrollView>
                }
                {/* <Input placeholder={''} value={d} /> */}
                <ComplianceTable logTitle={props.logTitle} siteId={props.siteId} />
              </View>
            </>
          </ScrollView>
        <AttachmentViewerModal
          visible={isViewerVisible}
          onClose={() => setIsViewerVisible(false)}
          fileName={viewerFileName}
          blob={viewerBlob}
          loading={viewerLoading}
        />
      </View>
  );
});

const styles = StyleSheet.create({
  container: {
    //justifyContent: 'center',
    //alignItems: 'center',
    paddingLeft: '5%',
    paddingRight: '5%',

    flex: 1,
    padding: 16,
    gap: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  formContainerPC: {
    paddingLeft: '25%',
    paddingRight: '25%',
  },
  formContainerMobile: {
    borderWidth: 0.5, // Set the border width
    borderColor: 'gray', // Set the border color
    borderRadius: 5, // Set the border radius (optional)
    backgroundColor: '#FFFFFF',
    shadowColor: 'blue', // Set the shadow color
    shadowOffset: { width: 0, height: 2 }, // Set the shadow offset
    shadowOpacity: 0.5, // Set the shadow opacity
    shadowRadius: 4, // Set the shadow radius
    elevation: 4,
    //marginLeft: '5%',
    //marginRight: '5%',
    //paddingLeft: '2%',
    //paddingRight: '2%',
  },
  input: {
    backgroundColor: 'transparent',
    marginVertical: 5,
    marginBottom: 30,
    borderWidth: 1,
    //borderWidth: 0.5, // Set the border width
    borderColor: 'gray', // Set the border color
    borderRadius: 5, // Set the border radius (optional)
    shadowColor: 'blue', // Set the shadow color
    shadowOffset: { width: 0, height: 2 }, // Set the shadow offset
    shadowOpacity: 0.5, // Set the shadow opacity
    shadowRadius: 4, // Set the shadow radius
    elevation: 4,
    minHeight: 60,
    //maxHeight: 60,
  },
  date: {
    minWidth: '100%',
    minHeight: 60,
    maxHeight: 60,

    backgroundColor: 'transparent',
    marginVertical: 5,
    marginBottom: 30,
    borderWidth: 1,
    //borderWidth: 0.5, // Set the border width
    borderColor: 'gray', // Set the border color
    borderRadius: 5, // Set the border radius (optional)
    shadowColor: 'blue', // Set the shadow color
    shadowOffset: { width: 0, height: 2 }, // Set the shadow offset
    shadowOpacity: 0.5, // Set the shadow opacity
    shadowRadius: 4, // Set the shadow radius
    elevation: 4
  },
  buttonsContainer: {
    flex: 1,
    //padding: 16,
    gap: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    //alignItems: 'center',
    //justifyContent: 'space-evenly',
    height: 60
  }
});

export default PaperForm;


